import { createContext, useContext, useEffect, useRef, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { sendAnalyticsEvent } from '../services/analyticsClient';
import { getOrCreateVisitorId } from '../services/visitorId';

interface AnalyticsContextValue {
  track: (eventName: string, properties?: Record<string, string | number | boolean>) => void;
  trackPageView: (path?: string, title?: string) => void;
}

const AnalyticsContext = createContext<AnalyticsContextValue | null>(null);

export function AnalyticsProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const prevPathRef = useRef<string>('');

  useEffect(() => {
    getOrCreateVisitorId();
  }, []);

  useEffect(() => {
    if (location.pathname !== prevPathRef.current) {
      prevPathRef.current = location.pathname;
      sendAnalyticsEvent({
        event_name: 'page_view',
        url_path: location.pathname,
        page_title: document.title,
      });
    }
  }, [location.pathname]);

  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      sendAnalyticsEvent({
        event_name: 'frontend_error',
        properties: {
          error_type: 'uncaught_error',
          message: event.message,
          filename: event.filename || 'unknown',
          line: event.lineno || 0,
          col: event.colno || 0,
        },
        error_message: event.message,
      });
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const message = event.reason?.message || String(event.reason);
      sendAnalyticsEvent({
        event_name: 'frontend_error',
        properties: {
          error_type: 'unhandled_rejection',
          message,
        },
        error_message: message,
      });
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  const track = (eventName: string, properties?: Record<string, string | number | boolean>) => {
    sendAnalyticsEvent({ event_name: eventName, properties });
  };

  const trackPageView = (path?: string, title?: string) => {
    sendAnalyticsEvent({
      event_name: 'page_view',
      url_path: path ?? window.location.pathname,
      page_title: title ?? document.title,
    });
  };

  return (
    <AnalyticsContext.Provider value={{ track, trackPageView }}>
      {children}
    </AnalyticsContext.Provider>
  );
}

export function useAnalytics(): AnalyticsContextValue {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error('useAnalytics must be used within AnalyticsProvider');
  }
  return context;
}
